import { Fragment, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useEventLog,
  type EventSeverity,
  type EventSource,
} from "@/hooks/use-event-log";
import { useUsers } from "@/hooks/use-users";
import { useProjects } from "@/hooks/use-projects";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

const SEVERITY_STYLES: Record<EventSeverity, string> = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  error: "bg-red-100 text-red-700",
};

const SOURCE_LABELS: Record<EventSource, string> = {
  user: "Bruker",
  system: "System",
  sync: "Synk",
};

const ENTITY_LABELS: Record<string, string> = {
  project: "Prosjekt",
  offer: "Tilbud",
  offer_line: "Tilbudslinje",
  budget_line: "Budsjett",
  budget_line_assignment: "Oppgave",
  assignment: "Tildeling",
  time_entry: "Timer",
  time_entries: "Timer",
  time_sessions: "Timer-økt",
  voucher: "Bilag",
  voucher_allocations: "Bilag-allokering",
  invoice: "Faktura",
  user: "Bruker",
  system_config: "Innstilling",
  project_comment: "Kommentar",
  role_menu_permission: "Meny-tilgang",
  project_role_permission: "Rolle-tilgang",
  feature_flag: "Funksjonsbryter",
  recurring_agreements: "Avtale",
  equipment_items: "Utstyr",
  equipment_bookings: "Utstyrsbooking",
  locations: "Lokasjon",
};

const PAGE_SIZE = 50;

function formatLogDate(d: string): string {
  return new Date(d).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function EventLogSection() {
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: users = [] } = useUsers();
  const { data: projects = [] } = useProjects();

  const { data, isLoading } = useEventLog({
    source: sourceFilter !== "all" ? (sourceFilter as EventSource) : undefined,
    severity:
      severityFilter !== "all" ? (severityFilter as EventSeverity) : undefined,
    entity_type: entityFilter !== "all" ? entityFilter : undefined,
    user_id: userFilter !== "all" ? userFilter : undefined,
    project_id: projectFilter !== "all" ? projectFilter : undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const events = data?.rows ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const resetFilters = () => {
    setSourceFilter("all");
    setSeverityFilter("all");
    setEntityFilter("all");
    setUserFilter("all");
    setProjectFilter("all");
    setSearch("");
    setPage(0);
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <SrIcon name="scroll-text" className="h-4 w-4 text-muted-foreground"  />
            <h2 className="font-display text-base">Hendelseslogg</h2>
            <span className="text-xs text-muted-foreground">
              {total > 0 ? `${total} treff` : ""}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Nullstill filtre
          </Button>
        </div>

        {/* Filtre */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          <div className="relative col-span-2 md:col-span-3 lg:col-span-2">
            <SrIcon name="search" className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"  />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Søk i hendelse…"
              className="h-8 text-xs pl-7"
            />
          </div>
          <Select
            value={sourceFilter}
            onValueChange={(v) => {
              setSourceFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle kilder</SelectItem>
              <SelectItem value="user">Bruker</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="sync">Synk</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={severityFilter}
            onValueChange={(v) => {
              setSeverityFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle nivå</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Advarsel</SelectItem>
              <SelectItem value="error">Feil</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={entityFilter}
            onValueChange={(v) => {
              setEntityFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle entiteter</SelectItem>
              {Object.entries(ENTITY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={userFilter}
            onValueChange={(v) => {
              setUserFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Bruker" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle brukere</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={projectFilter}
            onValueChange={(v) => {
              setProjectFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Prosjekt" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle prosjekter</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.project_code ? `${p.project_code} — ` : ""}
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabell */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Ingen hendelser funnet.
            </p>
          ) : (
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="w-6"></th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-32">
                    Tidspunkt
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-16">
                    Nivå
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-16">
                    Kilde
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                    Hendelse
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-28">
                    Bruker
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-24">
                    Entitet
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider w-32">
                    Prosjekt
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const isExpanded = expandedId === ev.id;
                  const hasPayload =
                    ev.payload && Object.keys(ev.payload).length > 0;
                  return (
                    <Fragment key={ev.id}>
                      <tr
                        className={cn(
                          "border-b border-border/30 last:border-0 hover:bg-muted/20",
                          hasPayload && "cursor-pointer",
                        )}
                        onClick={() =>
                          hasPayload &&
                          setExpandedId(isExpanded ? null : ev.id)
                        }
                      >
                        <td className="py-2 px-2 text-muted-foreground">
                          {hasPayload ? (
                            isExpanded ? (
                              <SrIcon name="chevron-down" className="w-3 h-3"  />
                            ) : (
                              <SrIcon name="chevron-right" className="w-3 h-3"  />
                            )
                          ) : null}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                          {formatLogDate(ev.created_at)}
                        </td>
                        <td className="py-2 px-2">
                          <Badge
                            variant="secondary"
                            className={cn(
                              "text-[10px]",
                              SEVERITY_STYLES[ev.severity],
                            )}
                          >
                            {ev.severity}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {SOURCE_LABELS[ev.source] ?? ev.source}
                        </td>
                        <td className="py-2 px-2 font-mono text-xs">
                          {ev.event_type}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground">
                          {ev.users?.full_name ?? "—"}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground text-xs">
                          {ev.entity_type
                            ? (ENTITY_LABELS[ev.entity_type] ?? ev.entity_type)
                            : "—"}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground text-xs">
                          {ev.project
                            ? `${ev.project.project_code ?? ""} ${ev.project.name ?? ""}`.trim()
                            : "—"}
                        </td>
                      </tr>
                      {isExpanded && hasPayload && (
                        <tr className="bg-muted/10">
                          <td colSpan={8} className="py-2 px-8">
                            <pre className="text-[11px] whitespace-pre-wrap break-all font-mono text-muted-foreground">
                              {JSON.stringify(ev.payload, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginering */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 text-xs">
            <span className="text-muted-foreground">
              Side {page + 1} av {lastPage + 1} ({total} hendelser)
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Forrige
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
              >
                Neste
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
